import { Link } from 'react-router-dom'
import { Button } from '@bizora/ui'
import { ArrowRight, Zap, Shield, BarChart, Package, ShoppingCart, Users } from 'lucide-react'

export function Home() {
  return (
    <>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="text-center">
          <h1 className="text-5xl lg:text-6xl font-bold text-neutral-900 mb-6">
            Your Shop,<br />
            <span className="bg-grad-primary bg-clip-text text-transparent">Online and On Time</span>
          </h1>
          <p className="text-xl text-neutral-600 mb-8 max-w-2xl mx-auto">
            A powerful multi-tenant SaaS platform that helps local businesses manage orders, 
            inventory, customers, and more—all in one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="w-full sm:w-auto">
                Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" size="lg" className="w-full sm:w-auto">
                Login
              </Button>
            </Link>
            <Link to="/demo">
              <Button variant="ghost" size="lg" className="w-full sm:w-auto">
                View Demo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Preview - Tailwind UI Style */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 bg-white">
        <div className="mx-auto max-w-2xl lg:text-center">
          <h2 className="text-base font-semibold leading-7 text-primary-600">Everything you need</h2>
          <p className="mt-2 text-4xl font-bold tracking-tight text-neutral-900 sm:text-5xl">
            A better way to run your business
          </p>
          <p className="mt-6 text-lg leading-8 text-neutral-600">
            Powerful features designed for local businesses. Manage orders, inventory, customers, and more—all in one intuitive platform.
          </p>
        </div>
        
        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
            <div className="flex flex-col">
              <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-neutral-900">
                <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-primary-600">
                  <Zap className="h-6 w-6 text-white" />
                </div>
                Fast & Reliable
              </dt>
              <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-neutral-600">
                <p className="flex-auto">
                  Lightning-fast performance with 99.9% uptime guarantee. Your business operations never stop, even during peak hours.
                </p>
              </dd>
            </div>

            <div className="flex flex-col">
              <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-neutral-900">
                <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-accent-400">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                Secure & Private
              </dt>
              <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-neutral-600">
                <p className="flex-auto">
                  Bank-level security with complete data isolation. Your customer data is encrypted and safe with us.
                </p>
              </dd>
            </div>

            <div className="flex flex-col">
              <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-neutral-900">
                <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-success-500">
                  <BarChart className="h-6 w-6 text-white" />
                </div>
                Real-time Analytics
              </dt>
              <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-neutral-600">
                <p className="flex-auto">
                  Track sales, orders, and customer behavior with detailed analytics and actionable insights.
                </p>
              </dd>
            </div>

            <div className="flex flex-col">
              <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-neutral-900">
                <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-purple-500">
                  <Package className="h-6 w-6 text-white" />
                </div>
                Multi-tenant Architecture
              </dt>
              <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-neutral-600">
                <p className="flex-auto">
                  Complete data isolation with custom branding per tenant. Perfect for agencies managing multiple clients.
                </p>
              </dd>
            </div>

            <div className="flex flex-col">
              <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-neutral-900">
                <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-warning-500">
                  <ShoppingCart className="h-6 w-6 text-white" />
                </div>
                Order Management
              </dt>
              <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-neutral-600">
                <p className="flex-auto">
                  Streamline your order processing from creation to fulfillment with real-time status updates.
                </p>
              </dd>
            </div>

            <div className="flex flex-col">
              <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-neutral-900">
                <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-info-500">
                  <Users className="h-6 w-6 text-white" />
                </div>
                Customer Management
              </dt>
              <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-neutral-600">
                <p className="flex-auto">
                  Build lasting relationships with comprehensive customer profiles, loyalty programs, and communication tools.
                </p>
              </dd>
            </div>
          </dl>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-grad-primary rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join hundreds of businesses already using Bizora to grow their online presence
          </p>
          <Link to="/register">
            <Button size="lg" variant="secondary">
              Start Your Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </>
  )
}

